import { motion } from 'framer-motion'
import { Zap, Infinity, Shield, Palette, Mic, FileText, Image, Video } from 'lucide-react'

const features = [
  {
    icon: Image,
    title: 'AI Image Generation',
    description: 'Create stunning images with FLUX, SDXL & more. 12 styles, 6 ratios, unlimited free generation.',
    color: 'from-primary to-primary/60',
  },
  {
    icon: Video,
    title: 'AI Video Generation',
    description: 'Generate cinematic videos with Seedance, Luma, Kling & Runway. Multiple styles and durations.',
    color: 'from-secondary to-secondary/60',
  },
  {
    icon: Mic,
    title: 'Text to Speech',
    description: 'Convert text to natural speech in 20+ languages using Web Speech API. Speed, pitch & voice control.',
    color: 'from-accent to-accent/60',
  },
  {
    icon: FileText,
    title: 'Script Writer',
    description: 'Generate professional scripts, stories, dialogues, marketing copy & social media posts with templates.',
    color: 'from-green-500 to-green-500/60',
  },
  {
    icon: Palette,
    title: 'Multiple Styles',
    description: 'From photorealistic to anime, cyberpunk to oil painting — endless creative possibilities.',
    color: 'from-purple-500 to-purple-500/60',
  },
  {
    icon: Zap,
    title: 'Lightning Fast',
    description: 'Optimized pipelines deliver results in seconds. No waiting, no queues.',
    color: 'from-amber-500 to-amber-500/60',
  },
  {
    icon: Infinity,
    title: 'Unlimited Free',
    description: 'Images are free unlimited. Video requires your own API key. No hidden fees or subscriptions.',
    color: 'from-pink-500 to-pink-500/60',
  },
  {
    icon: Shield,
    title: 'Privacy First',
    description: 'Your data stays in your browser. No server storage. No tracking. Fully private.',
    color: 'from-cyan-500 to-cyan-500/60',
  },
]

export function Features() {
  return (
    <section id="features" className="py-20 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            Why Choose Us
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
            Powerful Features
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Everything you need to create stunning content
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/20 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-secondary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                <feature.icon className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
